import { Vue, Component, Watch } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import { find, sortByProperty, truncateString } from '@/utils/utils';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import { searchAPI } from '@/providers/apis/search/search';
import { LEARNING_MAP_CONTENT_SEQUENCE, DEMO_USER_ACCESS_CATEGORIES_LIST, CAP_CATEGORIES, CAP_SUBJECTS } from '@/utils/constants';
import { sessionService } from '@/providers/services/auth/session';
import axios from 'axios';

@Component({
  name: 'learning-map-browser',
  components: {
    'material-icon': GoogleMaterialIcon,
  },
})

export default class LearningMapBrowser extends Vue {

  private get filterParams() {
    const classication = find(this.categories, 'isActive') || find(this.categories, 'is_default');
    const subject = find(this.subjectList, 'isActive') || find(this.subjectList, 'is_default');
    const course = find(this.courseList, 'isActive') ? find(this.courseList, 'isActive') : '';
    this.selectedItems = classication.title + ' / ' + subject.title + ' / ' + (course ? course.title : 'ALL LEVELS');
    const domainCode = this.domainList.filter(
      (item: any) => item.isActive)!.map((item: any) => item.id) || [];
    if (domainCode.length) {
      this.selectedItems += '/ ' + domainCode.length + 'Domains';
    }
    return {
      subjectClassification: classication ? classication.id : '',
      subjectCode: subject ? subject.id : '',
      courseCode: course ? course.id : '',
      domainCode: domainCode.length ? domainCode.toString() : '',
    };
  }
  private get contentList() {
    return {
      categories: this.categories,
      subjectList: this.subjectList,
      courseList: this.courseList,
      domainList: this.domainList,
    };
  }

  private get isSuperUser() {
    const session = sessionService.getSession();
    return session!.permissions.indexOf('ALL_ACCESS') !== -1;
  }

  // --------------------------------------------------------------------------
  // Properties

  private activeCategory: any = {};

  private activeSubject: any = {};

  private activeCourse: any = {};

  private selectedItems: string = '';

  private fetchedCompetencies: any = [];

  private competencies: any = [];

  private tableBody: any = [];

  private searchTerms: string = '';


  /**
   * @property {Array}
   * Property to store sequence of content items to show
   */
  private contentSequence = LEARNING_MAP_CONTENT_SEQUENCE;

  private categories: any = [];

  private subjectList: any = [];

  private courseList: any = [];

  private domainList: any = [];

  private tableData: any = [];

  private isActiveGenerateBtn: boolean = false;

  private demoCategories: any = DEMO_USER_ACCESS_CATEGORIES_LIST;

  @Watch('categories', { deep: true })
  private onChangeCategories(value: any) {
    const activeCategory = find(value, 'isActive') || find(value, 'is_default');
    this.activeCategory = activeCategory;
    this.courseList = [];
    this.domainList = [];
    this.onLoad(activeCategory.id);
  }

  @Watch('subjectList', { deep: true })
  private onChangeSujects(value: any) {
    this.courseList = [];
    this.domainList = [];
    this.isActiveGenerateBtn = true;
    const activeSubject = find(value, 'isActive') || find(value, 'is_default');
    if (activeSubject && activeSubject!.frameworks) {
      this.isActiveGenerateBtn = false;
      activeSubject.frameworkId = activeSubject.standard_framework_id;
      this.activeSubject = activeSubject;
      this.onCourseLoad(activeSubject);
    }

  }

  @Watch('courseList', { deep: true })
  private onChangeCourse(value: any) {
    const activeCourse: any = find(value, 'isActive') || find(value, 'is_default');
    if (activeCourse) {
      this.activeCourse = activeCourse;
      this.fetchDomains(activeCourse);
    }
  }

  @Watch('fetchedCompetencies', { deep: true })
  private onWatchCompetencies(value: any) {
    const microComptencyLevelPattern = 'learning_target_level_';
    this.tableBody = [];
    const promise = value.map((competency: any) => {
      const competencyLevel = competency.type.includes(microComptencyLevelPattern)
        ? 'micro-competency'
        : 'competency';
      const tableRow = {
        code: competencyLevel === 'competency' ? competency.code : null,
        id: competency.id,
        title: competency.title,
        contentCounts: this.getStructuredContentCount(
          competency.contentCounts,
        ),
        prerequisites: competency.prerequisites,
        competencyLevel,
      };
      this.tableBody.push(tableRow);
      return Promise.resolve(tableRow);
    });
    Promise.all(promise).then((data) => {
      if (this.$parent) {
        this.$parent.$data.isLoading = false;
      }
      this.$emit('onGenarate', {
        tableBody: this.tableBody,
        selectedItems: this.selectedItems,
      });
    });
  }

  // -----------------------------------------------------------------------------
  // Hooks
  private created() {
    this.$store.dispatch('activityStore/fetchCategory').then(() => {
      this.categories = this.isSuperUser ? this.demoCategories : this.$store.state.activityStore.categoryList;
      // Demo purpose for CAP library
      if (this.$access.isCapLibrary) {
        this.categories = this.categories.map((cat: any) => {
          cat.isDisabled = true;
          if (CAP_CATEGORIES.indexOf(cat.id as any) !== -1) {
            cat.isDisabled = false;
          }
          return cat;
        });
      }
    });
  }

  // ----------------------------------------------------------------------------------------
  // Actions
  private onClickItems(value: any, selectedItem: any, key: string) {
    const actitveItem = find(value, 'isActive');
    const isFramework = key === 'domainList';
    if (actitveItem && !isFramework) {
      this.$set(actitveItem, 'isActive', false);
    }
    const isActive = isFramework && selectedItem.isActive ? false : true;
    this.$set(selectedItem, 'isActive', isActive);
  }

  private generateTable() {
    if (this.$parent) {
      this.$parent.$data.isLoading = true;
    }
    searchAPI.fetchLearningMapCompetency(this.filterParams, 0, 500).then((content) => {
      this.fetchedCompetencies = content.competencyInfo;
      this.competencies.concat(this.fetchedCompetencies);
    });
  }

  private onSearch(searchTerms: string) {
    searchAPI.searchLearningMapCompetency(searchTerms, 0, 500).then((content) => {
      this.fetchedCompetencies = content.competencyInfo;
      this.competencies.concat(this.fetchedCompetencies);
      this.selectedItems = 'KEYWORD : ' + searchTerms;
    });
  }


  // -----------------------------------------------------------------------------------------
  // Methods

  private onLoad(categoryId: string) {
    let categoryIds = [categoryId];
    if (this.isSuperUser) {
      categoryIds = this.demoCategories.find((item: any) => item.id === categoryId).included;

    }
    const promiseList = categoryIds.map((id: any) => taxonomyAPI.fetchSubjects(id));
    axios.all(promiseList).then((subjects: any) => {
      this.subjectList = subjects.flat(1);
      if (this.$access.isCapLibrary) {
        this.subjectList.map((sub: any) => {
          sub.isDisabled = true;
          if (CAP_SUBJECTS.indexOf(sub.id) !== -1) {
            sub.isDisabled = false;
          }
          return sub;
        });
        this.subjectList = this.subjectList.sort((a: any, b: any) => {
          return CAP_SUBJECTS.indexOf(a.id) - CAP_SUBJECTS.indexOf(b.id);
        });
        sortByProperty(this.subjectList, 'isDisabled', 'ASC');
      }
    });
  }

  private onCourseLoad(activeSubject: any) {
    this.$store.dispatch('activityStore/fetchCourse', activeSubject).then(() => {
      this.courseList = this.$store.state.activityStore.courseList;
    });
  }

  private fetchDomains(course: any) {
    taxonomyAPI.fetchDomains(this.activeSubject.frameworkId, this.activeSubject.id, course.id).then((domain) => {
      this.domainList = domain;
    });
  }

  private activeClassName(value: any, items: any) {
    return find(value, 'isActive') ? items.isActive : items.is_default;
  }

  /**
   * @function getStructuredContentCount
   * Method to structurize content count of each competency
   */
  private getStructuredContentCount(contentCounts: any) {
    const controller = this;
    const structuredContentCount: any = [];
    const contentSequence = this.contentSequence.map((content) => {
      const contentCount = {
        type: content.type,
        count: contentCounts[`${content.type}`],
      };
      structuredContentCount.push(contentCount);
    });
    return structuredContentCount;
  }


  // private updateTableContent(rawData: any) {

  // }
}
